import React, { useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogActions,
    Typography,
    Button,
    Box,
    Avatar,
    CircularProgress
} from '@mui/material';
import ShareIcon from '@mui/icons-material/Share';
import { useTranslation } from 'react-i18next';
import Confetti from 'react-confetti';
import { shareImageService } from '../services/ShareImageService';

const GameWinnerModal = ({ open, winnerTeamId, players, gamePoints, onHome }) => {
    const { t } = useTranslation();
    const [isSharing, setIsSharing] = useState(false);

    // Determine winning players based on team ID
    // Team 1: Players 0 and 2
    // Team 2: Players 1 and 3
    const winningPlayers = winnerTeamId === 1
        ? [players[0], players[2]]
        : [players[1], players[3]];

    // Check if avatar is an emoji (string) or an image URL
    const isEmojiAvatar = (avatar) => {
        return typeof avatar === 'string' && !avatar.startsWith('http') && !avatar.startsWith('/');
    };

    // Handle share button click
    const handleShare = async () => {
        setIsSharing(true);
        try {
            await shareImageService.shareWinnerImage({
                winnerTeamId,
                players,
                gamePoints: gamePoints || 2,
                t
            });
        } catch (error) {
            console.error('Error sharing:', error);
        } finally {
            setIsSharing(false);
        }
    };

    return (
        <Dialog
            open={open}
            fullScreen
            PaperProps={{
                style: {
                    backgroundColor: 'rgba(0, 0, 0, 0.9)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center'
                }
            }}
        >
            {open && <Confetti recycle={true} numberOfPieces={200} />}

            <DialogContent sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                gap: 1.5,
                overflow: 'hidden',
                padding: '16px !important'
            }}>
                <Typography variant="h3" sx={{ color: '#FFD700', fontWeight: 'bold', textShadow: '0 0 20px rgba(255, 215, 0, 0.5)', fontSize: { xs: '2rem', sm: '2.5rem' } }}>
                    {t('game.winner_title')}
                </Typography>

                <Typography variant="h5" sx={{ color: 'white', fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                    {t('game.winner_team', { teamId: winnerTeamId })}
                </Typography>

                <Box sx={{ display: 'flex', gap: 3, mt: 1 }}>
                    {winningPlayers.map((player, index) => (
                        <Box key={index} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            {isEmojiAvatar(player?.avatar) ? (
                                <Box
                                    sx={{
                                        width: 80,
                                        height: 80,
                                        borderRadius: '50%',
                                        border: '3px solid #FFD700',
                                        boxShadow: '0 0 15px rgba(255, 215, 0, 0.5)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '2.5rem',
                                        backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                    }}
                                >
                                    {player?.avatar}
                                </Box>
                            ) : (
                                <Avatar
                                    src={player?.avatar}
                                    sx={{
                                        width: 80,
                                        height: 80,
                                        border: '3px solid #FFD700',
                                        boxShadow: '0 0 15px rgba(255, 215, 0, 0.5)'
                                    }}
                                />
                            )}
                            <Typography variant="body1" sx={{ color: 'white', mt: 0.5, fontWeight: 'bold', fontSize: '1rem' }}>
                                {player?.name || t('setup.player_placeholder', { number: index + 1 })}
                            </Typography>
                        </Box>
                    ))}
                </Box>

            </DialogContent>

            <DialogActions sx={{ pb: 3, pt: 0, flexDirection: 'column', gap: 2 }}>
                <Button
                    variant="contained"
                    size="large"
                    onClick={handleShare}
                    disabled={isSharing}
                    startIcon={isSharing ? <CircularProgress size={20} color="inherit" /> : <ShareIcon />}
                    sx={{
                        backgroundColor: '#4CAF50',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '1.1rem',
                        padding: '10px 40px',
                        borderRadius: '50px',
                        minWidth: '220px',
                        '&:hover': {
                            backgroundColor: '#45a049'
                        },
                        '&:disabled': {
                            backgroundColor: '#4CAF50',
                            color: 'rgba(255, 255, 255, 0.7)'
                        }
                    }}
                >
                    {isSharing ? t('share.sharing') : t('share.button')}
                </Button>
                <Button
                    variant="contained"
                    size="large"
                    onClick={onHome}
                    sx={{
                        backgroundColor: '#FFD700',
                        color: 'black',
                        fontWeight: 'bold',
                        fontSize: '1.1rem',
                        padding: '10px 40px',
                        borderRadius: '50px',
                        minWidth: '220px',
                        '&:hover': {
                            backgroundColor: '#FFC107'
                        }
                    }}
                >
                    {t('common.home')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default GameWinnerModal;
